import React, { Component } from 'react';
import PropTypes from 'prop-types';
import InputPrefix from 'components/connectedForm/InputPrefix';
import InputBlock from 'components/connectedForm/InputBlock';
import InputGroup from 'components/connectedForm/InputGroup';
import InputLabel from 'components/connectedForm/InputLabel';
import Select from 'components/react-select/Select';
import InputErrorMessage from 'components/connectedForm/InputErrorMessage';
import debounce from 'es6-promise-debounce';
import isFunction from 'components/react-data-grid/utils/isFunction';
import * as QueryHelper from '../../utils/QueryHelper';
import { ReadOnlyComponent } from './DropdownReadOnlyComponent';

export default class Dropdown extends Component {
  constructor(props) {
    super(props);
    this.state = {
      previousValue: '',
    };
    this.handleClearInput = this.handleClearInput.bind(this);
    this.handleOnChange = this.handleOnChange.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleOnBlur = this.handleOnBlur.bind(this);
    this.refreshQuery = this.refreshQuery.bind(this);
    this.loadOptions = debounce(this.refreshQuery, 150);
  }

  handleOnChange = (value, keyCode) => {
    let val = value;
    if (this.props.enum) {
      if (Array.isArray(val)) {
        val = val.map((wrappedValue) => wrappedValue.value);
      } else {
        val = val && val.value;
      }
    }
    if (this.props.search) {
      if (val.url) {
        this.props.history.push({
          pathname: val.url,
          state: { previousLocation: this.props.history.location?.pathname || 'back' },
        });
      }
    }

    if (this.props.deletable && Array.isArray(val) && val.length > 0) {
      const data = this.props.value ? [...this.props.value] : [];

      if (data.length > 0) {
        const updated = data.map((item) => {
          if (!val.find((changedItem) => changedItem.id === item.id)) {
            return { ...item, deleted: true };
          }
          return item;
        });

        const newItems = val.filter((item) => {
          return !data.find((changedItem) => changedItem.id === item.id);
        });

        val = updated.concat(newItems);
      }
    }

    if (this.props.onChange) {
      if (Array.isArray(val) && val.length === 0) val = null;
      if (this.props.valueOnly) {
        val = val && val[this.props.valueKey];
      }
      this.props.onChange(val, keyCode);
    }
  };

  handleInputChange = (val) => {
    if (this.props.remoteFilter) {
      this.setState({
        previousValue: val,
      });
    }
    return val;
  };

  refreshQuery = (input) => {
    if (this.props.refreshQuery) {
      return this.props.refreshQuery(input).then((response) => {
        let data = QueryHelper.processData(response.data);
        if (this.props.normalizeData && this.props.normalizeData instanceof Function) {
          data = this.props.normalizeData(data);
        }

        return { options: data, complete: true };
      });
    }
  };

  handleClearInput() {
    this.props.onChange(this.props.dataid, '');
    this.node.focus();
  }

  handleOnBlur = () => {
    if (this.props.onBlur) {
      const value = this.props.value && this.props.value !== '' ? this.props.value : null;
      this.props.onBlur(value);
    }
  };

  focus() {
    this.node.focus();
  }

  setDropdownComponent() {
    const {
      arrowRenderer,
      sidebarForm,
      selectModal,
      selectModalData,
      selectModalUpdateOpen,
      dataid,
      inGrid,
      multi,
      onFocus,
      options,
      optionRenderer,
      valueRenderer,
      filterOption,
      data,
      readOnly,
      remoteFilter,
      labelKey,
      labelKey2,
      valueKey,
      value,
      validationErrors,
      touched,
      tabIndex,
      autoFocus,
      placeholder,
      newOptionCreator,
      shouldKeyDownEventCreateNewOption,
      promptTextCreator,
      id,
      creatable,
      deletable,
      modalOpen,
      modalProps,
      setInputRef,
      menuContainerStyle,
      predefinedFilters,
      cache,
    } = this.props;

    if (readOnly) return <ReadOnlyComponent {...this.props} />;
    let SelectComponent = Select;
    let loadOptions = null;
    if (remoteFilter) {
      if (
        creatable ||
        (this.props.column && this.props.column.options.dropdown && this.props.column.options.dropdown.creatable)
      ) {
        SelectComponent = Select.AsyncCreatable;
      } else {
        SelectComponent = Select.Async;
      }
      loadOptions = this.props.loadOptions ? this.props.loadOptions : this.loadOptions;
    } else if (
      creatable ||
      (this.props.column && this.props.column.options.dropdown && this.props.column.options.dropdown.creatable)
    ) {
      SelectComponent = Select.Creatable;
    }

    const optionList = options ? options : data;
    const errors = validationErrors ? Object.keys(validationErrors) : [];
    const hasErrors = touched && errors.length > 0;

    // specifically set to true OR not an enum, not mandatory and clearable is truthy
    const clearable =
      this.props.clearable === true || (!this.props.enum && !this.props.mandatory && this.props.clearable);

    const renderValue = deletable && value && Array.isArray(value) ? value.filter((val) => !val.deleted) : value;

    return (
      <SelectComponent
        options={optionList}
        valueKey={valueKey}
        labelKey={labelKey}
        labelKey2={labelKey2}
        arrowRenderer={arrowRenderer}
        sidebarForm={sidebarForm}
        selectModal={selectModal}
        selectModalData={selectModalData}
        selectModalUpdateOpen={selectModalUpdateOpen}
        valueRenderer={valueRenderer}
        optionRenderer={optionRenderer}
        filterOption={filterOption}
        value={renderValue}
        name={dataid}
        onChange={this.handleOnChange}
        onInputChange={this.handleInputChange}
        onBlur={this.handleOnBlur}
        onFocus={onFocus}
        multi={multi}
        loadOptions={loadOptions}
        autoload={false}
        placeholder={placeholder}
        hasErrors={hasErrors}
        ref={(node) => {
          this.node = node;
          if (isFunction(setInputRef)) {
            setInputRef(node);
          } else if (setInputRef) {
            setInputRef.current = node;
          }
        }}
        readOnly={readOnly}
        inGrid={inGrid}
        tabIndex={tabIndex}
        clearable={clearable}
        autofocus={autoFocus}
        data-testid={this.props['data-testid'] || this.props.testId}
        newOptionCreator={newOptionCreator}
        shouldKeyDownEventCreateNewOption={shouldKeyDownEventCreateNewOption}
        promptTextCreator={promptTextCreator}
        id={id}
        remoteFilter={remoteFilter}
        updateCellState={this.props.updateCellState}
        modalOpen={modalOpen}
        modalProps={modalProps}
        rawLabel={this.props.rawLabel}
        scanQuery={this.props.scanQuery}
        menuContainerStyle={menuContainerStyle}
        predefinedFilters={predefinedFilters}
        renderInBody={this.props.renderInBody}
        menuRenderer={this.props.menuRenderer}
        filterOptions={this.props.filterOptions}
        onMenuScrollToBottom={this.props.onMenuScrollToBottom}
        totalOptionsCount={this.props.totalOptionsCount}
        query={this.props.query}
        cache={cache}
        customIcon={this.props.customIcon}
        entity={this.props.entity}
      />
    );
  }

  render() {
    const {
      active,
      id,
      label,
      includeLabel,
      inputPrefix,
      flexBox,
      labelCols,
      labelAddon,
      inputCols,
      readOnly,
      hint,
      validationErrors,
      showWhenNotActive,
      touched,
      customValidationErrors,
      mandatory,
      reformatLabel,
    } = this.props;

    const labelProps = {
      flexBox,
      id,
      includeLabel,
      hint,
      label,
      reformatLabel,
      mandatory,
      readOnly,
      labelAddon,
    };

    const SelectComponent = this.setDropdownComponent();

    if (this.props.inGrid || this.props.p4Field) return SelectComponent;

    return (
      <InputBlock flexBox={flexBox}>
        <InputLabel {...labelProps} cols={labelCols} />
        <InputGroup flexBox={flexBox} cols={includeLabel ? inputCols : 1} withLabel={includeLabel}>
          <InputPrefix inputPrefix={inputPrefix} />
          {SelectComponent}
          <InputErrorMessage
            label={label}
            active={active}
            touched={touched}
            validationErrors={validationErrors}
            showWhenNotActive={showWhenNotActive}
            customValidationErrors={customValidationErrors}
          />
        </InputGroup>
      </InputBlock>
    );
  }
}

Dropdown.propTypes = {
  active: PropTypes.bool,
  clearButton: PropTypes.bool,
  mandatory: PropTypes.bool,
  onChange: PropTypes.func,
  includeLabel: PropTypes.bool,
  inGrid: PropTypes.bool,
  inputPrefix: PropTypes.string,
  readOnly: PropTypes.bool,
  flexBox: PropTypes.bool,
  labelCols: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  inputCols: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  id: PropTypes.string,
  hint: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.array, PropTypes.number]),
  validationErrors: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  showWhenNotActive: PropTypes.bool,
  label: PropTypes.string,
  type: PropTypes.string,
  search: PropTypes.bool,
  creatable: PropTypes.bool,
  deletable: PropTypes.bool,
  rawLabel: PropTypes.bool, // option to disable label formatting (e.g. for allcaps fields).
  valueOnly: PropTypes.bool,
  enum: PropTypes.bool,
  valueKey: PropTypes.string,
  labelKey: PropTypes.string,
};
Dropdown.defaultProps = {
  active: false,
  mandatory: false,
  clearButton: false,
  placeholder: '',
  onChange: () => {},
  optionRenderer: null,
  filterOption: null,
  includeLabel: true,
  inGrid: false,
  inputPrefix: null,
  readOnly: false,
  flexBox: true,
  labelCols: 1 / 4,
  inputCols: 3 / 4,
  value: '',
  validationErrors: [],
  hint: '',
  showWhenNotActive: false,
  type: 'text',
  label: '',
  id: '',
  search: false,
  creatable: false,
  deletable: false,
  setInputRef: () => {},
  valueKey: 'value',
  labelKey: 'label',
  enum: false,
};
Dropdown.displayName = 'Dropdown';
